<template>
  <div class="extension-attributes-tab">
    <!-- Empty State -->
    <div
      v-if="mappings.length === 0"
      class="empty-state py-8 text-center text-gray-500">
      <p>{{ i18n.t('datasourceMapping.noMappingsConfigured') }}</p>
      <SmButton class="mt-4" @click="addMapping">
        + {{ i18n.t('datasourceMapping.addFirstMapping') }}
      </SmButton>
    </div>

    <!-- Mappings List -->
    <div v-else class="mappings-container">
      <!-- Desktop: Table Layout -->
      <div class="hidden sm:block">
        <div
          class="mb-2 grid grid-cols-12 gap-2 text-sm font-semibold text-gray-600">
          <div class="col-span-5">{{ i18n.t('datasourceMapping.microsoftAttribute') }}</div>
          <div class="col-span-1"></div>
          <div class="col-span-4">{{ i18n.t('datasourceMapping.saasmetrixField') }}</div>
          <div class="col-span-2"></div>
        </div>

        <div
          v-for="(mapping, index) in mappings"
          :key="index"
          class="mapping-row mb-3 grid grid-cols-12 items-center gap-2">
          <!-- Microsoft Attribute Dropdown -->
          <div class="col-span-5">
            <SmSelect
              :selection="
                getSelectionArray(mapping.microsoftAttribute, 'microsoft')
              "
              :options="availableMicrosoftAttributes"
              :placeholder="i18n.t('datasourceMapping.selectAttribute')"
              trigger-size="small"
              @change="
                (selected) =>
                  handleAttributeChange(index, 'microsoft', selected)
              " />
          </div>

          <!-- Arrow -->
          <div class="col-span-1 flex justify-center text-gray-400">→</div>

          <!-- SaaSMetrix Field Dropdown -->
          <div class="col-span-4">
            <SmSelect
              :selection="
                getSelectionArray(mapping.saasmetrixField, 'saasmetrix')
              "
              :options="availableSaasmetrixFields"
              :placeholder="i18n.t('datasourceMapping.selectField')"
              trigger-size="small"
              @change="
                (selected) =>
                  handleAttributeChange(index, 'saasmetrix', selected)
              " />
          </div>

          <!-- Delete Button -->
          <div class="col-span-2 flex justify-end">
            <button
              class="delete-btn hover:text-red-600 p-2"
              @click="removeMapping(index)"
              aria-label="Delete mapping">
              <Trash :size="18" />
            </button>
          </div>
        </div>
      </div>

      <!-- Mobile: Card Layout -->
      <div class="sm:hidden">
        <div
          v-for="(mapping, index) in mappings"
          :key="index"
          class="mapping-card mb-3 rounded border border-gray-200 p-4">
          <div class="mb-3">
            <label class="mb-1 block text-sm font-semibold">
              {{ i18n.t('datasourceMapping.microsoftAttribute') }}
            </label>
            <SmSelect
              :selection="
                getSelectionArray(mapping.microsoftAttribute, 'microsoft')
              "
              :options="availableMicrosoftAttributes"
              :placeholder="i18n.t('datasourceMapping.selectAttribute')"
              trigger-size="small"
              @change="
                (selected) =>
                  handleAttributeChange(index, 'microsoft', selected)
              " />
          </div>

          <div class="mb-3">
            <label class="mb-1 block text-sm font-semibold">
              {{ i18n.t('datasourceMapping.saasmetrixField') }}
            </label>
            <SmSelect
              :selection="
                getSelectionArray(mapping.saasmetrixField, 'saasmetrix')
              "
              :options="availableSaasmetrixFields"
              :placeholder="i18n.t('datasourceMapping.selectField')"
              trigger-size="small"
              @change="
                (selected) =>
                  handleAttributeChange(index, 'saasmetrix', selected)
              " />
          </div>

          <button
            class="text-red-600 flex items-center gap-1 text-sm"
            @click="removeMapping(index)">
            <Trash :size="16" />
            {{ i18n.t('datasourceMapping.remove') }}
          </button>
        </div>
      </div>

      <!-- Add Button -->
      <SmButton
        v-if="mappings.length > 0"
        class="ml-auto mt-4 w-full"
        @click="addMapping">
        + {{ i18n.t('datasourceMapping.addFirstMapping') }}
      </SmButton>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { Trash } from 'lucide-vue-next'
  import { useI18n } from 'vue-i18n'
  import {
    SAASMETRIX_EXTENSION_ATTRIBUTE_FIELDS,
    type SaasmetrixField,
    type SaasmetrixFieldValue,
  } from './extensionAttributeFields'

  const i18n = useI18n()

  type MicrosoftAttributeValue = `extensionAttribute${number}`

  interface SelectOption<T> {
    value: T
    label: string
  }

  interface Mapping {
    microsoftAttribute: string | null
    saasmetrixField: SaasmetrixField
  }

  const props = defineProps<{
    modelValue: Mapping[]
  }>()

  const emit = defineEmits<{
    'update:modelValue': [value: Mapping[]]
  }>()

  const mappings = computed({
    get: () => props.modelValue,
    set: (value) => emit('update:modelValue', value),
  })

  // Microsoft Extension Attributes 1-15
  const microsoftAttributes: SelectOption<MicrosoftAttributeValue>[] = Array.from(
    { length: 15 },
    (_, index) => ({
      value: `extensionAttribute${index + 1}` as MicrosoftAttributeValue,
      label: `Extension Attribute ${index + 1}`,
    })
  )

  const availableMicrosoftAttributes = computed(() => {
    const selectedAttributes = mappings.value
      .map((m) => m.microsoftAttribute)
      .filter(isNotNull)

    return microsoftAttributes.filter(
      (attr) => !selectedAttributes.includes(attr.value)
    )
  })

  // SaaSMetrix fields
  const saasmetrixFields = computed<SelectOption<SaasmetrixFieldValue>[]>(() => {
    return SAASMETRIX_EXTENSION_ATTRIBUTE_FIELDS.map((field) => ({
      value: field,
      label: i18n.t(`datasourceMapping.fields.${field}`),
    }))
  })

  const availableSaasmetrixFields = computed(() => {
    const selectedFields = mappings.value
      .map((m) => m.saasmetrixField)
      .filter(isNotNull)

    return saasmetrixFields.value.filter(
      (field) => !selectedFields.includes(field.value)
    )
  })

  function addMapping() {
    mappings.value = [
      ...mappings.value,
      { microsoftAttribute: null, saasmetrixField: null },
    ]
  }

  function removeMapping(index: number) {
    mappings.value = mappings.value.filter((_, i) => i !== index)
  }

  function getSelectionArray(
    value: string | null,
    type: 'microsoft' | 'saasmetrix'
  ): SelectOption<string>[] {
    if (!value) return []

    const source =
      type === 'microsoft' ? microsoftAttributes : saasmetrixFields.value
    const item = source.find((item) => item.value === value)

    return item ? [item as SelectOption<string>] : []
  }

  function handleAttributeChange(
    index: number,
    type: 'microsoft' | 'saasmetrix',
    selected: Array<SelectOption<string>> | undefined
  ): void {
    const value = selected?.[0]?.value ?? null

    if (type === 'microsoft') {
      mappings.value[index].microsoftAttribute = value
    } else {
      mappings.value[index].saasmetrixField = value as SaasmetrixField
    }
  }

  function isNotNull<T>(value: T | null): value is T {
    return value !== null
  }
</script>

<style scoped lang="scss">
  .extension-attributes-tab {
    min-height: 300px;
  }

  .mapping-row {
    transition: background-color 0.2s;

    &:hover {
      background-color: rgba(0, 0, 0, 0.02);
    }
  }

  .delete-btn {
    opacity: 0.5;
    transition: opacity 0.2s;

    &:hover {
      opacity: 1;
    }
  }
</style>
